import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import { INTERVIEW_TYPE_IDS } from 'features/sendouts/common/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import strings from 'strings';
import { TextBox, When } from 'UI/components/atoms';
import CustomDatePicker from 'UI/components/atoms/CustomDatePicker';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPToggleButton from 'UI/components/molecules/FPToggleButton';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { REQUIRED_VALIDATION } from 'UI/utils';

import { FORM_FIELDS_MAP } from '../../drawers/CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

import { TimezoneSelector } from './components/TimezoneSelector';
import { FixedHourFields, TimeRangeFields } from './components';
import { TIME_FRAME_MAP } from './InterviewSchedule.constants';
import { useStyles } from './InterviewSchedule.styled';

const STRINGS = strings.sendoutsV2.form.sections.interviewDetails;

const TIME_FRAME_OPTIONS = Object.values(TIME_FRAME_MAP);
const INTERVIEW_TYPE_ICON_MAP = {
  [INTERVIEW_TYPE_IDS.FaceToFace]: LocationOnOutlinedIcon,
  [INTERVIEW_TYPE_IDS.RemoteVideo]: VideocamOutlinedIcon
};

export const buildInterviewTypeOptions = interviewTypes =>
  interviewTypes.map(({ id, title }) => {
    const Icon = INTERVIEW_TYPE_ICON_MAP[id];

    return {
      value: id,
      title,
      icon: Icon ? <Icon /> : null
    };
  });

export const InterviewSchedule = () => {
  const classes = useStyles();
  const { control, errors, setValue, watch } = useFormContext();
  const { state: interviewTypesState } = useFetchWithStatusV2({
    endpoint: Endpoints.SendoutsInterviewTypes
  });

  const formValues = watch([
    FORM_FIELDS_MAP.InterviewDate.key,
    FORM_FIELDS_MAP.InterviewFromHour.key,
    FORM_FIELDS_MAP.InterviewTimeFrame.key,
    FORM_FIELDS_MAP.InterviewType.key
  ]);

  const interviewDate = formValues[FORM_FIELDS_MAP.InterviewDate.key];
  const interviewTime = formValues[FORM_FIELDS_MAP.InterviewFromHour.key];
  const interviewTimeFrame = formValues[FORM_FIELDS_MAP.InterviewTimeFrame.key];
  const isFixedHour = interviewTimeFrame?.id === TIME_FRAME_MAP.FixedHour.id;
  const isTimeRange = interviewTimeFrame?.id === TIME_FRAME_MAP.TimeRange.id;
  const isInterviewTypesLoading = interviewTypesState.status === UIStatus.Loading;
  const isInterviewTypesReady = interviewTypesState.status === UIStatus.Success;
  const interviewTypeOptions = isInterviewTypesReady
    ? buildInterviewTypeOptions(interviewTypesState.results)
    : [];
  const defaultInterviewType = interviewTypeOptions[0]?.value || null;

  const handleToggleChange = value => {
    setValue(FORM_FIELDS_MAP.InterviewType.key, value, true);
  };

  const handleDateChange = (name, value) => {
    setValue(name, value, true);
  };

  const handleControllerChange = useCallback(([, value]) => value, []);

  return (
    <>
      <Grid item xs={12}>
        <When condition={isInterviewTypesLoading}>
          <CircularProgress size={24} />
        </When>
        <When condition={isInterviewTypesReady}>
          <Controller
            name={FORM_FIELDS_MAP.InterviewType.key}
            control={control}
            rules={REQUIRED_VALIDATION}
            defaultValue={defaultInterviewType}
            as={
              <FormControl component="fieldset" className={classes.toggleGroupFullWidth}>
                <FPToggleButton
                  onChange={handleToggleChange}
                  options={interviewTypeOptions}
                  initialValue={defaultInterviewType}
                  size="large"
                />
              </FormControl>
            }
          />
        </When>
        <When condition={interviewTypesState.status === UIStatus.Error}>
          <EmptyPlaceholder title={strings.sendoutsV2.drawers.tabs.status.error} />
        </When>
        <When condition={interviewTypesState.status === UIStatus.Empty}>
          <EmptyPlaceholder title={strings.sendoutsV2.drawers.tabs.status.error} />
        </When>
      </Grid>
      <Grid item container spacing={1}>
        <Grid item xs={6}>
          <Controller
            name={FORM_FIELDS_MAP.InterviewDate.key}
            rules={REQUIRED_VALIDATION}
            defaultValue={null}
            control={control}
            as={
              <CustomDatePicker
                disablePast
                isClearable
                name={FORM_FIELDS_MAP.InterviewDate.key}
                error={!!errors[FORM_FIELDS_MAP.InterviewDate.key]}
                helperText={errors[FORM_FIELDS_MAP.InterviewDate.key]?.message}
                label={STRINGS.labels.date || null}
                value={interviewDate}
                onDateChange={handleDateChange}
                placeholder={STRINGS.labels.date}
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name={FORM_FIELDS_MAP.InterviewTimeFrame.key}
            control={control}
            rules={REQUIRED_VALIDATION}
            defaultValue={null}
            as={
              <AutocompleteSelect
                error={!!errors[FORM_FIELDS_MAP.InterviewTimeFrame.key]}
                errorText={errors[FORM_FIELDS_MAP.InterviewTimeFrame.key]?.message}
                placeholder={STRINGS.labels.timeFrame}
                options={TIME_FRAME_OPTIONS}
              />
            }
            onChange={handleControllerChange}
            onChangeName="onSelect"
            valueName="selectedValue"
          />
        </Grid>
        <When condition={isFixedHour}>
          <FixedHourFields />
        </When>
        <When condition={isTimeRange}>
          <TimeRangeFields />
        </When>
        <When condition={isFixedHour || isTimeRange}>
          <Grid item xs={isFixedHour ? 6 : 12}>
            <TimezoneSelector interviewDate={interviewDate} interviewTime={interviewTime} />
          </Grid>
        </When>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewType.key] === INTERVIEW_TYPE_IDS.FaceToFace
          }
        >
          <Grid item xs={12}>
            <Controller
              name={FORM_FIELDS_MAP.InterviewAddress.key}
              control={control}
              defaultValue=""
              as={
                <TextBox
                  label={STRINGS.labels.interviewAddress}
                  placeholder={STRINGS.labels.interviewAddress}
                />
              }
              onChange={handleControllerChange}
              onChangeName="onChange"
              valueName="value"
            />
          </Grid>
        </When>
        <When
          condition={
            formValues[FORM_FIELDS_MAP.InterviewType.key] === INTERVIEW_TYPE_IDS.RemoteVideo
          }
        >
          <Grid item xs={12}>
            <Controller
              name={FORM_FIELDS_MAP.InterviewLink.key}
              defaultValue=""
              control={control}
              as={
                <TextBox
                  label={STRINGS.labels.interviewLink}
                  placeholder={STRINGS.labels.interviewLink}
                />
              }
              onChange={handleControllerChange}
              onChangeName="onChange"
              valueName="value"
            />
          </Grid>
        </When>
      </Grid>
    </>
  );
};
